import React from 'react'
import { Platform } from 'react-native'
import { G, Path } from 'react-native-svg'
import type { MapDef } from '@warcarp/api-types'

type Decoration = NonNullable<MapDef['decorations']>[number]

/** Its own label, distinct from the help button's, so the two never collide. */
export const WARFISH_LOGO_LABEL = 'Warfish.net logo'

/** Splits a map's decorations into inert scenery and the Warfish.net logo parts. */
export function splitDecorations(decorations: MapDef['decorations']): { scenery: Decoration[]; logo: Decoration[] } {
  const scenery: Decoration[] = []
  const logo: Decoration[] = []
  for (const decoration of decorations ?? []) (decoration.role === 'warfish-logo' ? logo : scenery).push(decoration)
  return { scenery, logo }
}

/**
 * Authored scenery shared by the live board and static map previews. The
 * Warfish.net logo is drawn last, as one group; with `onLogoPress` it opens
 * the classics explainer.
 */
export const MapDecorations = React.memo(function MapDecorations({ decorations, onLogoPress }:
  Pick<MapDef, 'decorations'> & { onLogoPress?: () => void }) {
  const { scenery, logo } = splitDecorations(decorations)
  const paths = (items: Decoration[], interactive: boolean) => items.map(decoration => <Path key={`decoration-${decoration.id}`}
    d={decoration.shape} fill={decoration.fill} stroke="transparent" pointerEvents={interactive ? 'auto' : 'none'} />)
  return <>
    {paths(scenery, false)}
    {logo.length > 0 && (onLogoPress
      // Same props as a territory shape: react-native-svg forwards `accessible`
      // and `accessibilityRole` to the web DOM as invalid attributes.
      ? <G testID="warfish-logo" onPress={onLogoPress} accessibilityLabel={WARFISH_LOGO_LABEL}
          style={Platform.OS === 'web' ? ({ cursor: 'pointer' } as never) : undefined}>{paths(logo, true)}</G>
      : <G pointerEvents="none">{paths(logo, false)}</G>)}
  </>
})
